/**
 * The wildcard grammar and the pattern preview (`SC-F507`, backed by `SC-F411`).
 *
 * [compileWildcard] is a faithful port of `pkg/apitypes/wildcard.go:31-85` at the
 * pinned commit: `*` compiles to `[^.]*`, `**` to `.*`, `\x` to a literal `x`, a
 * dot to a literal dot, everything else to a quoted literal, anchored at both
 * ends. An empty pattern and a trailing lone backslash are compile errors
 * upstream, so they are refused here.
 *
 * That makes this an **advisory engine mirror** in the sense of `03` §3.14: it is
 * a second implementation of logic that lives in Sluice. The exemption to
 * "the control plane never decides a query" holds because a preview is not a
 * decision — nothing in any request path consults it, and an instance whose engine
 * disagrees still enforces its own answer. What we owe in exchange is the label,
 * which every panel built on this module carries verbatim: *advisory preview — the
 * instance's engine decides*.
 *
 * The one trap this file exists to make impossible is the dotted table name.
 * `resources.tables` is matched against the **bare relation name** and the
 * compiler escapes `.` to a literal, so `tables: ["shop.main.orders"]` can never
 * match anything — and Sluice's own recipes use that form, which makes those
 * recipes inert. [dottedFault] refuses it and [splitDotted] is the offer to fix
 * it.
 */

import type { PreviewTable } from "./schema-cache.js";

/** WildcardMatcher is a compiled pattern. */
export interface WildcardMatcher {
  test(value: string): boolean;
}

/** PatternFault is a refusal: the linter rule it corresponds to, and the runtime
 * consequence in the author's terms. `rule` is empty for a fault no `SC-F529` rule
 * covers because Sluice itself refuses it loudly at compile. */
export interface PatternFault {
  rule: string;
  message: string;
}

/**
 * compileWildcard compiles a pattern, or returns the fault that makes it
 * uncompilable. Both refusals are Sluice's own: an empty pattern and a trailing
 * lone backslash fail `CompileWildcard`, which fails the snapshot compile, which
 * leaves the instance serving its previous policy.
 */
export function compileWildcard(pattern: string): WildcardMatcher | PatternFault {
  if (pattern === "") {
    return {
      rule: "",
      message:
        "An empty pattern is a compile error in Sluice, and a snapshot that fails to compile leaves the instance serving its previous policy.",
    };
  }
  let body = "";
  for (let i = 0; i < pattern.length; i++) {
    const c = pattern[i]!;
    if (c === "\\") {
      const next = pattern[i + 1];
      if (next === undefined) {
        return {
          rule: "",
          message:
            "A trailing backslash is a compile error in Sluice. Write \\\\ for a literal backslash.",
        };
      }
      body += quoteMeta(next);
      i++;
    } else if (c === "*") {
      if (pattern[i + 1] === "*") {
        body += ".*";
        i++;
      } else {
        body += "[^.]*";
      }
    } else if (c === ".") {
      body += "\\.";
    } else {
      body += quoteMeta(c);
    }
  }
  try {
    const re = new RegExp(`^${body}$`);
    return { test: (value: string) => re.test(value) };
  } catch {
    return { rule: "", message: "This pattern could not be compiled." };
  }
}

/** isFault narrows [compileWildcard]'s result. */
export function isFault(result: WildcardMatcher | PatternFault): result is PatternFault {
  return "message" in result;
}

/**
 * dottedFault refuses a dotted value in a field matched against a bare name, and
 * says why in runtime terms rather than in grammar terms (`SC-F507`, rule `L01`).
 *
 * It is a hard refusal and not a warning because there is no reading of the value
 * that works: the wildcard compiler escapes the dot to a literal, so the pattern
 * is anchored against a relation name that cannot contain one.
 */
export function dottedFault(value: string, what: "table" | "column"): PatternFault | null {
  if (!value.includes(".")) return null;
  const bare = what === "table" ? "bare relation name" : "bare column name";
  return {
    rule: "L01",
    message: `${what === "table" ? "Tables" : "Columns"} are matched against the ${bare}, and the wildcard compiler escapes a dot to a literal — so this pattern can never match anything. Split the parts across catalog, schema and ${what}.`,
  };
}

/** DottedSplit is a dotted value taken apart into the three fields that can
 * actually match it. */
export interface DottedSplit {
  catalog: string;
  schema: string;
  table: string;
}

/**
 * splitDotted takes `shop.main.orders` apart into the three fields (`SC-F507`).
 *
 * A two-part value is read as `schema.table` and a one-part value as the table
 * alone, because that is what the shorter forms mean everywhere else in the DSL: a
 * table reference with no catalog only matches `*` or `**`, so the missing
 * left-hand parts are genuinely unconstrained rather than unknown. More than three
 * parts keeps the surplus in the leading catalog segment, so nothing the author
 * typed is silently dropped.
 */
export function splitDotted(value: string): DottedSplit {
  const parts = value.split(".").filter((p) => p !== "");
  if (parts.length >= 3) {
    return {
      catalog: parts.slice(0, parts.length - 2).join("."),
      schema: parts[parts.length - 2]!,
      table: parts[parts.length - 1]!,
    };
  }
  if (parts.length === 2) return { catalog: "", schema: parts[0]!, table: parts[1]! };
  return { catalog: "", schema: "", table: parts[0] ?? "" };
}

/** PatternScope names which axis of the schema cache a pattern list is matched
 * against. It mirrors `internal/policy/selector.go:495-524`: catalogs against the
 * catalog half of a reference, schemas against the schema, tables against the bare
 * relation name, columns against the column name. */
export type PatternScope = "catalogs" | "schemas" | "tables" | "columns";

/**
 * PatternPreview is what a pattern list currently resolves to.
 *
 * `state` distinguishes the three answers `SC-S507` requires to look different:
 * `matches` with entries, `none` — an explicit "matches nothing", which is a real
 * and dangerous state rather than an empty list — and `unknown`, where the cache
 * has nothing to answer from and the preview says so instead of showing a possibly
 * wrong result.
 */
export interface PatternPreview {
  state: "matches" | "none" | "unknown" | "unconstrained";
  /** The concrete names the pattern set resolves to, sorted and de-duplicated. */
  names: string[];
  /** How many were found in total, so a truncated list can say "of 812". */
  total: number;
}

/** How many resolved names a preview lists before truncating (`SC-S507`). */
export const PREVIEW_LIMIT = 50;

/**
 * resolvePatterns lists what a pattern set covers on this instance right now.
 *
 * An empty pattern list is `unconstrained` rather than `none`: an absent list
 * matches everything, and reporting "matches nothing" for it would invert the
 * meaning of the most consequential omission in the DSL.
 */
export function resolvePatterns(
  scope: PatternScope,
  patterns: string[],
  tables: PreviewTable[],
  cacheEmpty: boolean,
): PatternPreview {
  const usable = patterns.filter((p) => p !== "");
  if (usable.length === 0) return { state: "unconstrained", names: [], total: 0 };
  if (cacheEmpty) return { state: "unknown", names: [], total: 0 };

  const matchers = usable
    .map(compileWildcard)
    .filter((m): m is WildcardMatcher => !isFault(m));
  if (matchers.length === 0) return { state: "unknown", names: [], total: 0 };

  const seen = new Set<string>();
  for (const candidate of candidates(scope, tables)) {
    if (matchers.some((m) => m.test(candidate))) seen.add(candidate);
  }
  const names = [...seen].sort();
  if (names.length === 0) return { state: "none", names: [], total: 0 };
  return { state: "matches", names: names.slice(0, PREVIEW_LIMIT), total: names.length };
}

/**
 * resolveClauseTables lists the fully-qualified tables a resource selector's
 * catalog / schema / table patterns jointly cover.
 *
 * `columns`, `tags` and `actions` are deliberately not applied. Columns do not
 * constrain table matching at all (`internal/policy/selector.go:521-523`), tags
 * expand through another object's rules and would need the whole draft, and actions
 * are checked above the selector. Folding any of them in would make the preview
 * describe a narrowing the engine does not perform.
 */
export function resolveClauseTables(
  resources: {
    catalogs?: string[];
    schemas?: string[];
    tables?: string[];
  },
  tables: PreviewTable[],
): { names: string[]; total: number } {
  const axes: Array<[PatternScope, string[] | undefined, (t: PreviewTable) => string]> = [
    ["catalogs", resources.catalogs, (t) => t.catalog],
    ["schemas", resources.schemas, (t) => t.schema],
    ["tables", resources.tables, (t) => t.table],
  ];
  const active = axes
    .map(([, patterns, pick]) => {
      const usable = (patterns ?? []).filter((p) => p !== "");
      if (usable.length === 0) return null;
      const matchers = usable.map(compileWildcard).filter((m): m is WildcardMatcher => !isFault(m));
      return matchers.length === 0 ? null : { matchers, pick };
    })
    .filter((a): a is { matchers: WildcardMatcher[]; pick: (t: PreviewTable) => string } => a !== null);

  const hits = tables
    .filter((t) => active.every(({ matchers, pick }) => matchers.some((m) => m.test(pick(t)))))
    .map((t) => `${t.catalog}.${t.schema}.${t.table}`)
    .sort();
  return { names: hits.slice(0, PREVIEW_LIMIT), total: hits.length };
}

/** candidates projects the schema cache onto one axis. */
function candidates(scope: PatternScope, tables: PreviewTable[]): Set<string> {
  const out = new Set<string>();
  for (const t of tables) {
    switch (scope) {
      case "catalogs":
        out.add(t.catalog);
        break;
      case "schemas":
        out.add(t.schema);
        break;
      case "tables":
        out.add(t.table);
        break;
      case "columns":
        for (const c of t.columns) out.add(c);
        break;
    }
  }
  return out;
}

/** quoteMeta escapes one character for a regular expression, the way Go's
 * `regexp.QuoteMeta` does for the same input set. */
function quoteMeta(c: string): string {
  return /[\\^$.|?*+()[\]{}]/.test(c) ? `\\${c}` : c;
}
